import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { findTradeHub, TRADE_HUBS, BUSINESS, type HubSubTopic } from "@/lib/site-data";
import { AnswerBlock } from "@/components/AnswerBlock";

const BASE_URL = "https://lehitradessaeo.com";

export const Route = createFileRoute("/hubs/$trade")({
  loader: ({ params }) => {
    const hub = findTradeHub(params.trade);
    if (!hub) throw notFound();
    return { hub };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Hub not found" }] };
    const h = loaderData.hub;
    const url = `${BASE_URL}/hubs/${params.trade}`;
    const title = `${h.title} | ${BUSINESS.shortName}`;
    const description = `${h.tagline} ${h.intro.slice(0, 110)}`.slice(0, 158);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: h.title,
            description,
            url,
            isPartOf: { "@id": `${BASE_URL}/#website` },
            about: { "@type": "Thing", name: `${h.trade} marketing` },
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["[data-speakable=\"question\"]", "[data-speakable=\"answer\"]"],
            },
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: h.subTopics.length,
              itemListElement: h.subTopics.map((t, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: t.title,
                url: `${BASE_URL}${t.href}`,
              })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Hubs", item: `${BASE_URL}/hubs` },
              { "@type": "ListItem", position: 3, name: h.trade, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-3xl font-black">Hub not found</h1>
      <p className="mt-2 text-muted-foreground">
        <Link to="/hubs" className="underline">See all trade hubs</Link>
      </p>
    </main>
  ),
  component: TradeHubPage,
});

function TradeHubPage() {
  const { hub } = Route.useLoaderData();
  const others = TRADE_HUBS.filter((h) => h.slug !== hub.slug);
  return (
    <main className="mx-auto max-w-4xl px-6 py-20">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
        {hub.trade} · Trade Marketing Hub
      </p>
      <h1 className="mt-3 font-display text-5xl tracking-tight md:text-6xl">{hub.title}</h1>
      <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{hub.tagline}</p>

      <AnswerBlock
        question={hub.answer.question}
        answer={hub.answer.answer}
        bullets={hub.answer.bullets}
      />

      <p className="mt-6 text-base leading-relaxed text-foreground/90">{hub.intro}</p>

      <section className="mt-12">
        <h2 className="font-display text-2xl tracking-tight">Silos in this hub</h2>
        <ul className="mt-6 grid gap-3 md:grid-cols-2">
          {hub.subTopics.map((t: HubSubTopic) => (
            <li key={t.href}>
              <a
                href={t.href}
                className="group block rounded-xl border border-border bg-card p-5 transition hover:border-primary/40"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-lg tracking-tight">{t.title}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{t.blurb}</p>
                  </div>
                  <span className="ml-3 text-primary transition group-hover:translate-x-1">→</span>
                </div>
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-16 rounded-2xl border border-border bg-card p-8">
        <h3 className="font-display text-xl tracking-tight">Other trade hubs</h3>
        <ul className="mt-3 flex flex-wrap gap-2 text-sm">
          {others.map((h) => (
            <li key={h.slug}>
              <Link
                to="/hubs/$trade"
                params={{ trade: h.slug }}
                className="inline-flex rounded-full border border-border px-3 py-1.5 text-muted-foreground hover:border-primary/40 hover:text-foreground"
              >
                {h.trade}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <div className="mt-16 rounded-2xl border border-border bg-card p-8 text-center">
        <h3 className="font-display text-2xl tracking-tight">
          Want this hub run for your {hub.trade.toLowerCase()} business?
        </h3>
        <p className="mt-3 text-muted-foreground">
          Free local SEO audit. No pitch deck, no contract pressure.
        </p>
        <Link
          to="/contact"
          className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Get my free audit
        </Link>
      </div>
    </main>
  );
}